<template>
  <DSPicker
    ref="pickerRef"
    v-model="isOpen"
    :placement="popoverPlacement"
    :offset="popoverOffset"
    v-bind="fullPickerAttributes"
    @escape="restoreActivatorFocus"
  >
    <template #activator="{ setRef, isOpen: pickerIsOpen, toggle }">
      <DSButton
        :ref="component => setActivatorRef(component as ComponentPublicInstance | null, setRef)"
        :variant="activatorVariant"
        :size="DS_SIZE.Medium"
        :prefix-icon="hasDisplayReactions ? undefined : 'smile'"
        :disabled="props.disabled"
        :aria-label="activatorLabel"
        :aria-pressed="hasOwnReaction"
        :aria-controls="isOpen ? panelId : undefined"
        :aria-expanded="isOpen"
        aria-haspopup="dialog"
        data-cy="ds-emoji-picker__activator"
        @click="onActivatorClick(pickerIsOpen, toggle)"
      >
        <template v-if="hasDisplayReactions">
          <span aria-hidden="true">
            <span
              v-for="emoji in displayReactions"
              :key="emoji.unicode"
            >
              {{ emoji.native }}
            </span>
          </span>
          <span>{{ reactionCount }}</span>
        </template>
      </DSButton>
    </template>

    <DSEmojiPickerPanel
      v-if="showFullPicker"
      :id="panelId"
      v-model:category="categoryModel"
      v-model:search="searchModel"
      :sections="visibleSections"
      :has-more-emojis="hasMoreEmojis"
      @hover="setHoveredEmoji"
      @select="selectEmoji"
      @load-more="loadMoreEmojis"
    />
    <DSEmojiPickerFastSelect
      v-else
      :id="panelId"
      :popup-label="popupLabel"
      :emojis="fastSelectItems"
      @select="selectEmoji"
      @show-all="showAllEmojis"
    />

    <template
      v-if="showFullPicker"
      #footer
    >
      <DSEmojiPickerFooter
        :preview-emoji="previewEmoji"
        :skin-tone="skinTone"
        :tone-options="toneOptions"
        @select="selectSkinTone"
      />
    </template>
  </DSPicker>
</template>

<script setup lang="ts">
import { computed, type ComponentPublicInstance } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";
import DSEmojiPickerFastSelect from "@/src/design_system/button_and_controls/emoji_picker/components/DSEmojiPickerFastSelect.vue";
import DSEmojiPickerFooter from "@/src/design_system/button_and_controls/emoji_picker/components/DSEmojiPickerFooter.vue";
import DSEmojiPickerPanel from "@/src/design_system/button_and_controls/emoji_picker/components/DSEmojiPickerPanel.vue";
import { useEmojiPickerController } from "@/src/design_system/button_and_controls/emoji_picker/composables/useEmojiPickerController";
import { useEmojiPickerPanel } from "@/src/design_system/button_and_controls/emoji_picker/composables/useEmojiPickerPanel";
import { useEmojiPickerReactions } from "@/src/design_system/button_and_controls/emoji_picker/composables/useEmojiPickerReactions";
import { DS_EMOJI_PICKER_CLASSES } from "@/src/design_system/button_and_controls/emoji_picker/constants/classes";
import type {
  DSEmojiPickerProps,
  DSEmojiPickerReaction,
} from "@/src/design_system/button_and_controls/emoji_picker/types/props";
import DSPicker from "@/src/design_system/shared_modules/picker/components/DSPicker.vue";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import { DEFAULT_FAST_SELECT_EMOJIS } from "@/src/shared/emoji/constants";
import type { EmojiSelection } from "@/src/shared/emoji/types";
import { resolveEmojiUnicodes } from "@/src/shared/emoji/utils";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<DSEmojiPickerProps>(), {
  disabled: false,
  fastSelect: false,
  fastSelectEmojis: () => [...DEFAULT_FAST_SELECT_EMOJIS],
});

const modelValue = defineModel<DSEmojiPickerReaction[]>({ default: () => [] });

const OPEN_LABEL = window.I18n.designSystem.emojiPicker.openLabel;
const REACTION_SUMMARY = window.I18n.designSystem.emojiPicker.reactionSummary;
const DEFAULT_FAST_SELECT_ITEMS = resolveEmojiUnicodes(DEFAULT_FAST_SELECT_EMOJIS);

const { displayReactions, hasDisplayReactions, hasOwnReaction, reactionCount, updateOwnReaction } =
  useEmojiPickerReactions({ modelValue });
const activatorVariant = computed(() => {
  return hasOwnReaction.value ? DS_BUTTON_VARIANT.Secondary : DS_BUTTON_VARIANT.Ghost;
});
const popupLabel = computed(() => props.ariaLabel || OPEN_LABEL);
const activatorLabel = computed(() => {
  if (!hasDisplayReactions.value) return popupLabel.value;

  const reactionGlyphs = displayReactions.value.map(emoji => emoji.native).join(" ");
  const template = reactionCount.value === 1 ? REACTION_SUMMARY.one : REACTION_SUMMARY.other;
  return template
    .replace("%{label}", () => popupLabel.value)
    .replace("%{emojis}", () => reactionGlyphs)
    .replace("%{count}", () => String(reactionCount.value));
});
const fastSelectItems = computed(() => {
  const resolvedEmojis = resolveEmojiUnicodes(props.fastSelectEmojis);
  return resolvedEmojis.length > 0 ? resolvedEmojis : DEFAULT_FAST_SELECT_ITEMS;
});

const {
  category,
  hasMoreEmojis,
  loadMoreEmojis,
  previewEmoji,
  refreshRecentEmojis,
  rememberEmoji,
  resetPickerState,
  search,
  selectCategory,
  selectSkinTone,
  setHoveredEmoji,
  skinTone,
  toneOptions,
  updateSearch,
  visibleSections,
} = useEmojiPickerPanel();

const {
  closeAndFocus,
  focus,
  isOpen,
  onActivatorClick,
  panelId,
  pickerRef,
  popoverOffset,
  popoverPlacement,
  restoreActivatorFocus,
  setActivatorRef,
  showAllEmojis,
  showFullPicker,
} = useEmojiPickerController({
  disabled: () => props.disabled,
  fastSelect: () => props.fastSelect,
  onPrepare: () => {
    refreshRecentEmojis();
    resetPickerState();
  },
});

const fullPickerAttributes = computed(() => {
  if (!showFullPicker.value) return {};

  return {
    id: panelId,
    class: DS_EMOJI_PICKER_CLASSES.Panel,
    role: "dialog",
    "aria-label": popupLabel.value,
    "data-cy": "ds-emoji-picker__panel",
  };
});

const categoryModel = computed({
  get: () => category.value,
  set: selectCategory,
});
const searchModel = computed({
  get: () => search.value,
  set: updateSearch,
});

const selectEmoji = (selection: EmojiSelection): void => {
  rememberEmoji(selection.unicode);
  updateOwnReaction(selection.unicode);
  closeAndFocus();
};

defineExpose({ focus });
</script>
